import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
    PlayerPointsLimiters,
    isBrowser,
    SetupRevealGames,
    getConfigForComponent,
    Coupon,
    TeleScript,
    InProgressPromotionsDisplay,
    ErrorMessage,
    getCMSObject,
    DisplayContent,
} from 'sg-ui-components';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { mapStateToProps, mapDispatchToProps } from '../../Store';
import PostClaimBox from './PostClaimBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import siteConfig from '../../promotionConfig';
import { HashLink } from 'react-router-hash-link';
import AllocatableEntriesForm from '../AllocatableEntries/AllocatableEntriesForm';
import pluralize from 'pluralize';
import { titleCase } from 'title-case';

import './style.scss';
const MOTIVES = {
    CUSTOM_URLS: 'CustomUrls',
    FACEBOOK: 'facebook',
    TWITTER: 'twitter',
    YOUTUBE: 'youtube',
    INSTAGRAM: 'instagram',
    SURVEY: 'Survey',
    TICKET_ENTRY: 'TicketEntry',
    SCAN_TICKET: 'ScanTicket',
};
/**********************************************************************
 * Component: PostClaim
 * Purpose:   Shows rewards from Bonusing, orders them by type and
 *            sets up any reveal games for the user to play as well
 *            as displaying limiter information.
 *
 * Props: -   config - site configuration information
 *            revealToken -  API token for the game server
 *            actions - actions table for api calls
 *            bonusing -  reference to the bonusing store
 *            loading - reference to the loading store
 *
 * APIs used:  getRevealToken() - gets the reveal gameserver token
 */
export const PostClaim = ({ config, user, revealToken, actions, bonusing, points, loading }) => {
    const [ticketError, setTicketError] = useState(null);
    const [noRewardError, setNoRewardError] = useState(false);
    const [lastActionInfo, setLastActionInfo] = useState({});
    const [pageLoaded, setPageLoaded] = useState(false);
    const postClaimMessages = getConfigForComponent('postClaim', config);

    const location = useLocation();
    const history = useHistory();
    const motive = isBrowser() ? location?.state?.motive : '';
    const imgUrl =
        (config.config.image_host && (config.config.image_host.startsWith('https://') ? config.config.image_host : `https://${config.config.image_host}`)) ||
        '';

    const telescriptPlayerPointsLimiters = getCMSObject('data.components.teleScripts.playerPointsLimiters.jsonBlock');

    useEffect(() => {
        async function fetchRevealToken() {
            await actions.revealTokenActions.getRevealToken();
            await actions.userActions.getPlayerDataFromAuthToken();
        }

        fetchRevealToken();

        const updateDashboard = async () => {
            if (user?.loggedIn && user?.player?.actions?.length === 0) {
                await actions.userActions.getDashboard();
            }
        };

        updateDashboard();

        // for Testing if Ticket Services is down, you can just test Post Claim with stubbed data
        // via the Bonusing Store and useing the sample BAR object.
        /* async function fetchDetails() {
                await actions.bonusingActions.getBarObject();
            }

            fetchDetails();
            */
        // end for testing
        setPageLoaded(true);

        /************************************************************************************************************************
         * Motives are passed in via the Redirect parameters to Post Claim to tell the component what action causes us to
         * reach the post-claim page (took a survey, clicked social media, entered ticket, etc).   Based upon the motive, the
         * page can display different links and messaging to thank the user for their action and guide the user to the next
         * thing to do (enter another ticket, go home, take another survey, etc). The motives can be customized via
         * the CMS.   As More features get added to Ensemble, this list of motives and options should grow.
         *
         */
        let motiveInfo = {
            linkTo: '',
            linkText: '',
            congratulationsMessage: '',
            action: '',
            moreInfoLink: '',
            moreInfoText: '',
            triggerType: '',
        };
        const juristictionName = typeof config?.site?.jurisdictionName === 'string' ? config?.site?.jurisdictionName : '';
        const postClaimMotives = getCMSObject('data.components.teleScripts.postClaimMotives.jsonBlock');

        // Might be more Motives later,  still a requirements TBD
        switch (motive) {
            case MOTIVES.FACEBOOK:
                motiveInfo.linkTo = postClaimMotives?.facebookLinkTo ?? '/home';
                motiveInfo.linkText = postClaimMotives?.facebookLinkText ?? 'Go Home';
                motiveInfo.congratulationsMessage =
                    postClaimMotives?.facebookCongratulationsMessage ?? 'Thank you for Liking ' + juristictionName + ' Lottery on Facebook.';
                motiveInfo.congratulationsMessageSubtitle =
                    postClaimMotives?.facebookCongratulationsMessageSubtitle ?? 'You qualified for the following promotions.';
                motiveInfo.triggerType = postClaimMotives?.facebookTriggerType ?? 'Like on Facebook';
                break;

            case MOTIVES.TWITTER:
                motiveInfo.linkTo = postClaimMotives?.twitterLinkTo ?? '/home';
                motiveInfo.linkText = postClaimMotives?.twitterLinkText ?? 'Go Home';
                motiveInfo.congratulationsMessage =
                    postClaimMotives?.twitterCongratulationsMessage ?? 'Thank you for following ' + juristictionName + ' Lottery on Twitter.';
                motiveInfo.congratulationsMessageSubtitle =
                    postClaimMotives?.twitterCongratulationsMessageSubtitle ?? 'You qualified for the following promotions.';
                motiveInfo.triggerType = postClaimMotives?.twitterTriggerType ?? 'Follow on Twitter';
                break;

            case MOTIVES.YOUTUBE:
                motiveInfo.linkTo = postClaimMotives?.youtubeLinkTo ?? '/home';
                motiveInfo.linkText = postClaimMotives?.youtubeLinkText ?? 'Go Home';
                motiveInfo.congratulationsMessage =
                    postClaimMotives?.youtubeCongratulationsMessage ?? 'Thank you for following ' + juristictionName + ' Lottery on YouTube.';
                motiveInfo.congratulationsMessageSubtitle =
                    postClaimMotives?.youtubeCongratulationsMessageSubtitle ?? 'You qualified for the following promotions.';
                motiveInfo.triggerType = postClaimMotives?.youtubeTriggerType ?? 'Follow on YouTube';
                break;

            case MOTIVES.INSTAGRAM:
                motiveInfo.linkTo = postClaimMotives?.instagramLinkTo ?? '/home';
                motiveInfo.linkText = postClaimMotives?.instagramLinkText ?? 'Go Home';
                motiveInfo.congratulationsMessage =
                    postClaimMotives?.instagramCongratulationsMessage ?? 'Thank you for following ' + juristictionName + ' Lottery on Instagram.';
                motiveInfo.congratulationsMessageSubtitle =
                    postClaimMotives?.instagramCongratulationsMessageSubtitle ?? 'You qualified for the following promotions.';
                motiveInfo.triggerType = postClaimMotives?.instagramTriggerType ?? 'Follow on Instagram';
                break;

            case MOTIVES.SURVEY:
                motiveInfo.linkTo = postClaimMotives?.surveyLinkTo ?? '/home';
                motiveInfo.linkText = postClaimMotives?.surveyLinkText ?? 'Go Home';
                motiveInfo.congratulationsMessage = postClaimMotives?.surveyCongratulationsMessage ?? 'Thank you for completing our survey.';
                motiveInfo.congratulationsMessageSubtitle =
                    postClaimMotives?.surveyCongratulationsMessageSubtitle ?? 'You qualified for the following promotions.';
                motiveInfo.triggerType = postClaimMotives?.surveyTriggerType ?? 'Survey';
                break;

            case MOTIVES.TICKET_ENTRY:
                motiveInfo.linkTo = postClaimMotives?.ticketEntruLinkTo ?? '/enter/';
                motiveInfo.linkText = postClaimMotives?.ticketEntryLinkText ?? 'Enter Another Ticket';
                motiveInfo.congratulationsMessage =
                    postClaimMotives?.ticketEntryCongratulationsMessage ?? 'Congratulations! <br> Your Ticket Has Been' + ' Accepted!';
                motiveInfo.congratulationsMessageSubtitle =
                    postClaimMotives?.ticketEntryCongratulationsMessageSubtitle ?? 'Your ticket entry qualified for the following promotions.';
                motiveInfo.action = postClaimMotives?.ticketEntryTriggerType ?? 'entry';
                break;

            case MOTIVES.SCAN_TICKET:
                motiveInfo.linkTo = postClaimMotives?.scanEntryLinkTo ?? 'scanner';
                motiveInfo.linkText = postClaimMotives?.scanEntryLinkText ?? 'Enter Another Ticket';
                motiveInfo.congratulationsMessage =
                    postClaimMotives?.scanEntryCongratulationsMessage ?? 'Congratulations! <br> Your Ticket Has Been' + ' Accepted!';
                motiveInfo.congratulationsMessageSubtitle =
                    postClaimMotives?.scanEntryCongratulationsMessageSubtitle ?? 'Your ticket entry qualified for the following promotions.';
                motiveInfo.action = postClaimMotives?.scanTicketTriggerType ?? 'entry';
                break;
            case MOTIVES.CUSTOM_URLS:
                motiveInfo.linkTo = postClaimMotives?.customUrlsLinkTo ?? '/home';
                motiveInfo.linkText = postClaimMotives?.customUrlsLinkText ?? 'Go Home';
                motiveInfo.congratulationsMessage = postClaimMotives?.customUrlsCongratulationsMessage ?? 'Congratulations! You completed the achievement!';
                motiveInfo.congratulationsMessageSubtitle = postClaimMotives?.customUrlsCongratulationsMessageSubtitle ?? 'You have earned:';
                motiveInfo.triggerType = postClaimMotives?.customUrlsTriggerType ?? 'Custom URL Qualifier';
                break;

            default:
                motiveInfo.linkTo = postClaimMotives?.defaultLinkTo ?? '/enter/';
                motiveInfo.linkText = postClaimMotives?.defaultLinkText ?? 'Enter Another Ticket';
                motiveInfo.congratulationsMessage = postClaimMotives?.defaultCongratulationsMessage ?? 'Congratulations! Your ticket has been accepted!';
                motiveInfo.congratulationsMessageSubtitle = postClaimMotives?.congratulationsMessageSubtitle ?? 'You qualified for the following promotions.';
                motiveInfo.action = postClaimMotives?.defaultTriggerType ?? 'entry';
        }
        setLastActionInfo(motiveInfo);

        return () => {
            actions.bonusingActions.reset();
            setPageLoaded(false);
            setTicketError(null);
            setNoRewardError(false);
            setLastActionInfo({});
        };
    }, []);

    useEffect(() => {
        // Check if User Won anything
        if (bonusing.error) {
            setTicketError(bonusing.error);
        } else if (
            bonusing.awardedPromotions &&
            bonusing.inProgressPromotions &&
            bonusing.awardedPromotions.length === 0 &&
            bonusing.inProgressPromotions.length === 0
        ) {
            setNoRewardError(true);
        }
    }, [bonusing]);

    //***********************************************************************************
    // Cleans up store data and send user to destination based on how they got their
    // rewards.
    const onLinkBack = (e) => {
        e.preventDefault();
        actions.entryActions.reset();
        actions.drawTicketActions.reset();
        actions.scratchTicketActions.reset();
        actions.bonusingActions.reset();
        history.push(lastActionInfo.linkTo);
    };

    //***********************************************************************************
    // Component for a Allocated Entries Sweepstake Award.
    //
    const AllocatableRewardsDisplay = () => {
        return (
            <div className='container-fluid'>
                {bonusing.allocatableRewards.map((rewardDisplay, index) => {
                    const legacyImage = imgUrl + '/' + config?.site?.imgFolderName + '/promotion_' + rewardDisplay.promotionId + '_thumb.png';
                    return (
                        <div key={index}>
                            <PostClaimBox
                                imageUrl={rewardDisplay.content?.thumb ?? legacyImage}
                                name={rewardDisplay.promotionName ?? 'prize'}
                                currency={rewardDisplay.promoReward?.reward?.currency ?? ''}
                                prize_name={rewardDisplay.promoReward?.reward?.prize_name ?? ''}
                                intent_amount={rewardDisplay.promoReward?.reward?.intent_amount ?? 0}
                                actual_amount={rewardDisplay.promoReward?.reward?.actual_amount ?? 0}
                                multiplier={rewardDisplay.promoReward?.reward?.multiplier ?? 1}
                                isAllocatable={true}
                            />
                            <AllocatableEntriesForm
                                sweepId={rewardDisplay.promoReward?.reward.sku ?? ''}
                                promotionName={rewardDisplay.promotionName ?? ''}
                                numEntries={rewardDisplay.promoReward?.reward?.actual_amount ?? ''}
                                entryId={rewardDisplay.promoReward?.reward?.entry_id ?? ''}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }; // end AllocatableRewardsDisplay

    //***********************************************************************************
    // Component for a rewarded item.   Shows the promotion image with what they won
    // and a link (if applicable) for more details.
    //
    const RewardsDisplay = ({ rewardsArray }) => {
        return (
            <div>
                {rewardsArray.map((rewardDisplay, index) => {
                    let promotionError = '';
                    const image = imgUrl + '/' + config?.site?.imgFolderName + '/promotion_' + rewardDisplay.promoReward.reward.promotion_id + '_thumb.png';
                    if (rewardDisplay.promoReward.reward.state === 'failed') {
                        promotionError = 'Reward pending. Please check back later.';
                    } else if (rewardDisplay.promoReward.reward.actual_amount < rewardDisplay.promoReward.reward.intent_amount) {
                        promotionError =
                            rewardDisplay.promoReward.reward.actual_amount == 0
                                ? 'Your reward could not be fulfilled at this time'
                                : 'Your reward could only be partially awarded at this time';
                    }
                    return (
                        <PostClaimBox
                            key={index}
                            imageUrl={image ?? ''}
                            name={rewardDisplay.promotionName ?? ''}
                            currency={rewardDisplay.promoReward.reward.currency ?? ''}
                            prize_name={rewardDisplay.promoReward.reward.prize_name ?? ''}
                            amount={rewardDisplay.promoReward.reward.actual_amount ?? ''}
                            promotionError={promotionError ?? ''}
                        />
                    );
                })}
            </div>
        );
    }; // end PointRewards

    //***********************************************************************************
    // Component for a coupon rewards.   Shows the promotion image with what they won
    // and a button that will popup the coupon details (which then can be printed ).   A
    // link is also displayed that will take the user to their full coupon history.
    //
    const CouponsDisplay = () => {
        return (
            <div className='container-fluid'>
                {bonusing.couponRewards.map((rewardDisplay, index) => {
                    let promotionError = '';
                    const image =
                        rewardDisplay.content?.thumb ?? imgUrl + '/' + config?.site?.imgFolderName + '/promotion_' + rewardDisplay.promotionId + '_thumb.png';
                    if (rewardDisplay.promoReward.reward.state === 'failed') {
                        promotionError = postClaimMessages?.telescript?.rewardPendingMsg ?? 'Reward pending. Please check back later.';
                    } else if (rewardDisplay.promoReward.reward.actual_amount < rewardDisplay.promoReward.reward.intent_amount) {
                        promotionError =
                            rewardDisplay.promoReward.reward.actual_amount == 0
                                ? postClaimMessages?.telescript?.rewardNotFulfilledMsg ?? 'Your reward could not be fulfilled at this time'
                                : postClaimMessages?.telescript?.rewardPartialFilledMsg ?? 'Your reward could only be partially awarded at this time';
                    }
                    return (
                        <div key={index}>
                            <div className='media my-2 text-center claim-box' key={rewardDisplay.promotionName ?? ''}>
                                <div className='container-fluid'>
                                    <div className='row'>
                                        <div className='col-md-6 px-0'>
                                            {image && <img className='promo-image' src={`${image}`} alt={`${rewardDisplay.promotionName ?? ''} logo`} />}
                                        </div>
                                        <div className='col-md-6'>
                                            <div className='media-body promotion-info-container'>
                                                <h2 className='promotion-name'>{rewardDisplay.promotionName ?? ''}</h2>
                                                {promotionError ? <p className='mt-0 error'>{promotionError}</p> : null}
                                                <p className='mt-1 reward-amount'>{rewardDisplay.promoReward?.reward?.actual_amount ?? ''}</p>
                                                <p className='mt-1 reward-currency'>
                                                    {pluralize(
                                                        titleCase(rewardDisplay.promoReward?.reward?.description ?? ''),
                                                        rewardDisplay.promoReward?.reward?.actual_amount ?? '',
                                                        false
                                                    )}
                                                </p>
                                                <HashLink to='/my-activity#coupons-history'>View Coupon History</HashLink>
                                                <Coupon
                                                    className='Coupon'
                                                    lottery_logo={getCMSObject('data.sitewideSettings.alternateLogo.image.imageSrc')}
                                                    reward_id={rewardDisplay.promoReward.reward.reward_id.toString() ?? index.toString()}
                                                    prize_name={rewardDisplay.promoReward.reward.display_name ?? rewardDisplay.promoReward.reward.prize_name}
                                                    prize_description={rewardDisplay.promoReward.reward.prize_description ?? ''}
                                                    redeem={rewardDisplay.promoReward.reward.redeem ?? ''}
                                                    expiration={rewardDisplay.promoReward.reward.expiration.toString() ?? ''}
                                                    state={rewardDisplay.promoReward.reward.state ?? ''}
                                                    Url={rewardDisplay.promoReward.reward.Url ?? ''}
                                                    barcodeHost={config.config.barcode_image_host ?? ''}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    //***********************************************************************************
    // Component for a button that takes the user back to where they came from that caused
    // the reward to fire
    //
    const LinkBack = () => {
        return (
            <Link to={lastActionInfo.linkTo} className='btn theme-btn theme-secondary' onClick={(e) => onLinkBack(e)}>
                {lastActionInfo.linkText}
            </Link>
        );
    };

    //* Need to provide additional data for IGR games like Snowball fight
    const additionalRevealGameData = {
        player: {
            player_id: user?.player?.registrar_id ?? '',
            email: user?.player?.email ?? '',
            name: user?.player?.name ?? '',
            first_name: user?.player?.first_name ?? '',
            last_name: user?.player?.last_name ?? '',
        },
    };

    //***********************************************************************************
    // Congratulations/Success message for the action that caused the reward.
    //
    const CongratulationsMessage = () => {
        return (
            <>
                {/* only show a congratulations for promos in progress or awarded */}
                {bonusing.awardedPromotions.length > 0 || bonusing.promosInProgress.length > 0 ? (
                    <div>
                        <h1 className='congratulation-message' dangerouslySetInnerHTML={{ __html: lastActionInfo.congratulationsMessage }} />
                        <h1 className='congratulation-message' dangerouslySetInnerHTML={{ __html: lastActionInfo.congratulationsMessageSubtitle }} />
                    </div>
                ) : (
                    <div>
                        <h1 className='congratulation-message'>Your ticket does not currently qualify for any promotion.</h1>
                        <p>You may save this ticket and try again when a new promotion is listed.</p>
                    </div>
                )}
            </>
        );
    };

    if (
        !pageLoaded ||
        loading.actions['allocateEntries'] ||
        loading.actions['getSweeps'] ||
        loading.actions['getActiveSweeps'] ||
        loading.actions['enterDrawTicket'] ||
        loading.actions['enterScratchTicket']
    ) {
        return null;
    } else {
        return (
            <div className='rewards-area'>
                {ticketError ? (
                    <div className='row m-1'>
                        <div className='col text-center '>
                            <ErrorMessage code={ticketError} collection='data.messages.ticketErrorMessages.jsonBlock' />
                            <p className='text-center'>
                                <LinkBack />
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        {noRewardError ? (
                            <div className='row m-1'>
                                <div className='col text-center'>
                                    <p className='my-3'>
                                        <FontAwesomeIcon className='error-tooltip' icon='fa-solid fa-circle-exclamation' /> Your {lastActionInfo.triggerType}{' '}
                                        Does Not Qualify For Any Current Promotions Or Reward
                                    </p>
                                    <TeleScript line={postClaimMessages?.postClaimMessages?.telescript?.actionNotQualifiedMsg}>
                                        <p>
                                            Check the <Link to='/promotions'>My Rewards</Link> list to see what actions qualify.
                                        </p>
                                        <p className='text-center'>
                                            <LinkBack />
                                        </p>
                                    </TeleScript>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className='row'>
                                    <div className='col px-md-5 text-center'>
                                        <CongratulationsMessage motive={motive} />
                                    </div>
                                </div>
                                <SetupRevealGames bonusing={bonusing} revealToken={revealToken} config={config} additionalData={additionalRevealGameData} />
                                {/* Allocatable entries rewards should always be first shown */}
                                <DisplayContent note='Show won allocatable entry rewards' isVisible={config?.web?.features?.allocatableEntries}>
                                    <AllocatableRewardsDisplay />
                                </DisplayContent>
                                <DisplayContent note='Show experientialRewards' isVisible={bonusing.experientialRewards?.length}>
                                    <RewardsDisplay rewardsArray={bonusing.experientialRewards} />
                                </DisplayContent>
                                <DisplayContent note='Show won sweepstake rewards' isVisible={bonusing.sweepRewards?.length}>
                                    <RewardsDisplay rewardsArray={bonusing.sweepRewards} />
                                </DisplayContent>
                                <DisplayContent note='Show won coupon rewards' isVisible={config?.web?.features?.coupons && bonusing.couponRewards?.length}>
                                    <CouponsDisplay />
                                </DisplayContent>
                                <DisplayContent note='Show promo code rewards display' isVisible={bonusing.promoCodeRewards.length}>
                                    <RewardsDisplay rewardsArray={bonusing.promoCodeRewards} />
                                </DisplayContent>
                                <DisplayContent note='Show won point rewards' isVisible={config?.web?.features?.points && bonusing.pointRewards?.length}>
                                    <RewardsDisplay rewardsArray={bonusing.pointRewards} />
                                </DisplayContent>
                                <DisplayContent note='Show in progress promotions display' isVisible={bonusing.promosInProgress?.length}>
                                    <InProgressPromotionsDisplay
                                        promosInProgress={bonusing.promosInProgress}
                                        siteConfg={siteConfig}
                                        legacyImageDomain={imgUrl}
                                    />
                                </DisplayContent>
                                <DisplayContent note='Show play points limiters' isVisible={points?.totals?.find && points?.totals?.length}>
                                    <div className='row'>
                                        <div className='col text-center my-4'>
                                            <PlayerPointsLimiters
                                                actions={actions}
                                                points={points}
                                                telescript={telescriptPlayerPointsLimiters}
                                                options={{ pointReference: config?.site?.references?.pointReference }}
                                            />
                                        </div>
                                    </div>
                                </DisplayContent>
                                <div className={`row`}>
                                    <div className='col-md-4 offset-md-4 enter-another my-4 text-center'>
                                        <LinkBack />
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        ); // end return
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(PostClaim);
